import type { DealV2 } from '../types';
import { stageIndex } from './stageOrder';

/**
 * The board's sort vocabulary. These two aliases lived in `utils/sortDeals.ts` alongside the v1
 * sorter until 2026-09-02, when that file was deleted with the v1 `Deal` type it was written against
 * (`/api/pipeline` now serves Intel's sectors and nothing else). They are re-homed here rather than
 * dropped because `PipelineV2Page` and `DealTableV2` both type their sort state on them.
 */
export type SortKey = 'company' | 'stage' | 'days' | 'ev' | 'est_investment' | 'lead';
export type SortDirection = 'asc' | 'desc';

/**
 * The v1 sorter's rules over v2 rows. It was separate from `sortDeals` because that one was typed to
 * `Deal` and, more to the point, had to parse `"$120M"` back into a number with `parseEvToNumber` —
 * v2's values are already numeric, so the parse (and the class of bug that comes with it) is simply
 * gone. v1's sorter has since been deleted; this is now the only one.
 *
 * Same tiebreak chain as v1, deliberately: stage-most-advanced, then name A–Z, direction-independent.
 */
function tiebreak(a: DealV2, b: DealV2): number {
  const stageCmp = stageIndex(a.stage) - stageIndex(b.stage);
  if (stageCmp !== 0) return stageCmp;
  return a.name.localeCompare(b.name);
}

export function sortDealsV2(deals: DealV2[], key: SortKey, direction: SortDirection): DealV2[] {
  const sorted = [...deals];
  const dir = direction === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    let primary = 0;
    switch (key) {
      case 'company':
        primary = a.name.localeCompare(b.name);
        break;
      case 'stage':
        primary = stageIndex(a.stage) - stageIndex(b.stage);
        break;
      case 'days':
        primary = (a.days_in_stage ?? -1) - (b.days_in_stage ?? -1);
        break;
      case 'ev':
        primary = (a.enterprise_value ?? 0) - (b.enterprise_value ?? 0);
        break;
      case 'est_investment':
        primary = (a.est_investment ?? 0) - (b.est_investment ?? 0);
        break;
      case 'lead':
        primary = (a.deal_lead_name ?? '').localeCompare(b.deal_lead_name ?? '');
        break;
    }
    if (primary !== 0) return primary * dir;
    return tiebreak(a, b);
  });

  return sorted;
}
